import './table.scss';

import { Table, type TableColumnProps, type TableProps } from 'antd';
import { type FilterValue, type SorterResult } from 'antd/es/table/interface';
import {
  Children,
  type ReactElement,
  type ReactNode,
  cloneElement
} from 'react';
import { Link } from 'react-router';

import { useWindowSize } from '@/hooks/useWindowSize';

import AllurLoader from '../allur-loader';

interface IProps<T> extends Omit<TableProps<T>, 'onRow' | 'onChange'> {
  onRowClick?: (row: T) => void;
  rowLink?: (row: T) => string | undefined;
  onChange?: (
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: { action: 'paginate' | 'sort' | 'filter' }
  ) => void;
}

const CustomTable = <T extends object>({
  onRowClick,
  rowLink,
  ...props
}: IProps<T>) => {
  const [width] = useWindowSize();

  const checkedPagination =
    (props.pagination &&
      typeof props.pagination !== 'boolean' &&
      props.pagination) ||
    undefined;
  const current = checkedPagination?.current || 1;
  const pageSize = checkedPagination?.pageSize || 10;
  const total = checkedPagination?.total || 0;
  const minAmount = (current - 1) * pageSize + 1;
  const maxAmount = Math.min(current * pageSize, total);

  // Функция для рекурсивного прохода по `children`
  const processChildren = (children: ReactNode): ReactNode => {
    return Children.map(children, (child) => {
      if (!child || typeof child !== 'object' || !('type' in child)) {
        return child; // Пропускаем не-React элементы
      }

      const childElement = child as ReactElement<
        TableColumnProps<T>,
        string | React.ComponentType<TableColumnProps<T>>
      >;

      // Проверяем, является ли элемент `Table.Column`
      const isColumn =
        childElement.type &&
        (typeof childElement.type === 'function' ||
          typeof childElement.type === 'object') &&
        'name' in childElement.type &&
        (childElement.type as { name: string }).name === 'Column';

      if (isColumn) {
        const columnProps = childElement.props;
        const render = columnProps.render;

        return cloneElement(childElement, {
          render: (value: unknown, record: T, index: number) => {
            const href = rowLink?.(record);
            const originalRender: ReactNode = render
              ? (render(value, record, index) as ReactNode)
              : (value as ReactNode);

            return href ? (
              <Link
                to={href}
                onClick={(e) => {
                  if (onRowClick) e.preventDefault();
                }}
                data-table-link="true"
              >
                {originalRender}
              </Link>
            ) : (
              originalRender
            );
          },
          children: processChildren(columnProps.children)
        } as Partial<TableColumnProps<T>>);
      }

      return cloneElement(childElement, {
        children: processChildren(childElement.props.children)
      } as Partial<TableColumnProps<T>>);
    });
  };

  return (
    <Table<T>
      {...props}
      columns={props.columns?.map((item) => {
        return {
          ...item,
          render: (value, record, index) => {
            const href = rowLink?.(record);
            const render =
              item && item.render ? (
                item.render(value, record, index)
              ) : (
                <>{value}</>
              );

            return href ? (
              <Link
                to={href}
                onClick={(e) => {
                  if (onRowClick) e.preventDefault();
                }}
                data-table-link="true"
              >
                {render as ReactNode}
              </Link>
            ) : (
              render
            );
          }
        };
      })}
      size="small"
      showSorterTooltip={false}
      className={'table ' + props.className}
      rowClassName={`table__row ${onRowClick ? 'table__row--clickable' : ''}`}
      style={{
        '--pagination-text': `"${minAmount} - ${maxAmount} из ${total}"`
      }}
      pagination={{
        placement: ['bottomStart'],
        showSizeChanger: true,
        simple: width < 640,
        ...props.pagination,
        pageSizeOptions: [10, 20, 50],
        locale: {
          items_per_page: ''
        }
      }}
      onChange={(pagination, filters, sorter, extra) => {
        if (
          extra?.action === 'paginate' &&
          typeof props.pagination !== 'boolean'
        ) {
          props.pagination?.onChange?.(
            pagination.current || 1,
            pagination.pageSize || 10
          );
        } else {
          props.onChange?.(filters, sorter, extra);
        }
      }}
      scroll={{
        x: 'max-content'
      }}
      onRow={(r) => {
        return {
          onClick: () => {
            onRowClick?.(r);
          }
        };
      }}
      loading={{
        spinning: !!props.loading,
        indicator: <AllurLoader fullWidth />
      }}
    >
      {props.children && processChildren(props.children)}
    </Table>
  );
};

export default CustomTable;
